import {AxiosInstance} from "axios";
import {AppointmentType} from "@/core/interfaces/AppointmentType";

export interface AppointmentTypeStoreRequest {
    name: string;
    duration: number;
    color?: string;
    icon?: string | null;
    /** Refused by the API on an Apodis template, null included. */
    default_description?: string | null;
    is_bookable_online?: boolean;
    position?: number;
}

export type AppointmentTypeUpdateRequest = Partial<AppointmentTypeStoreRequest>;

export interface AppointmentTypeIndexOptions {
    withHidden?: boolean;
}

const AppointmentTypeResource = (apiClient: AxiosInstance) => {
    return {
        /**
         * Get the organization appointment types, ordered by position.
         * Templates hidden by the pharmacy are only included with `withHidden`.
         */
        async index(organizationId: number, options: AppointmentTypeIndexOptions = {}): Promise<AppointmentType[]> {
            const response = await apiClient.get(`organizations/${organizationId}/appointment-types`, {
                params: options.withHidden ? {with_hidden: 1} : undefined
            });

            return response.data.data;
        },

        /**
         * Create an appointment type for the organization.
         */
        async create(organizationId: number, request: AppointmentTypeStoreRequest): Promise<AppointmentType> {
            const response = await apiClient.post(`organizations/${organizationId}/appointment-types`, {data: request});

            return response.data.data;
        },

        /**
         * Update an appointment type. On an Apodis template, the change only
         * applies to the current pharmacy and the API refuses `name`, `position`
         * and `default_description`.
         */
        async update(appointmentTypeId: number, request: AppointmentTypeUpdateRequest): Promise<AppointmentType> {
            const response = await apiClient.patch(`appointment-types/${appointmentTypeId}`, {data: request});

            return response.data.data;
        },

        /**
         * Show again an Apodis template hidden by the pharmacy.
         *
         * Kept apart from update() on purpose: sending `hidden` on a template
         * the pharmacy never customized would create an override and freeze
         * the current catalog values for it.
         */
        async unhide(appointmentTypeId: number): Promise<AppointmentType> {
            const response = await apiClient.patch(`appointment-types/${appointmentTypeId}`, {data: {hidden: false}});

            return response.data.data;
        },

        /**
         * Soft delete a pharmacy's own type, or hide an Apodis template for the
         * pharmacy. Existing appointments keep their type either way.
         */
        async delete(appointmentTypeId: number): Promise<void> {
            await apiClient.delete(`appointment-types/${appointmentTypeId}`);
        }
    };
};

export default AppointmentTypeResource;
